/**
 * Displacement map for the glass lens. Red shifts X, green shifts Y;
 * mid-grey (128) stays still. Strongest at the rounded rim, calm in the middle.
 * Chromium applies this to the backdrop through the SVG filter in App.vue.
 */
function sdRoundBox(px: number, py: number, w: number, h: number, radius: number): number {
  const x = Math.abs(px - w / 2) - w / 2 + radius;
  const y = Math.abs(py - h / 2) - h / 2 + radius;
  const ax = Math.max(x, 0);
  const ay = Math.max(y, 0);
  return Math.min(Math.max(x, y), 0) + Math.hypot(ax, ay) - radius;
}

export function buildLensMap(): string {
  const w = 520;
  const h = 564;
  const radius = 40;
  const bezel = 72;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(w, h);
  const data = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const d = sdRoundBox(x + 0.5, y + 0.5, w, h, radius);
      const dist = -d;
      let mag = 0;
      let nx = 0;
      let ny = 0;
      if (dist > 0) {
        const t = Math.min(dist / bezel, 1);
        const s = t * t * (3 - 2 * t);
        mag = 1 - s;
        const e = 0.75;
        const gx = sdRoundBox(x + 0.5 + e, y + 0.5, w, h, radius) - sdRoundBox(x + 0.5 - e, y + 0.5, w, h, radius);
        const gy = sdRoundBox(x + 0.5, y + 0.5 + e, w, h, radius) - sdRoundBox(x + 0.5, y + 0.5 - e, w, h, radius);
        const len = Math.hypot(gx, gy) || 1;
        nx = gx / len;
        ny = gy / len;
      }
      const i = (y * w + x) * 4;
      data[i] = Math.round(Math.min(1, Math.max(0, 0.5 + 0.5 * -nx * mag)) * 255);
      data[i + 1] = Math.round(Math.min(1, Math.max(0, 0.5 + 0.5 * -ny * mag)) * 255);
      data[i + 2] = 128;
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL('image/png');
}

export const GLASS_LENS_MAP = typeof document === 'undefined' ? '' : buildLensMap();
